import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  NzTabChangeEvent,
  NzTabComponent,
  NzTabPosition,
  NzTabsComponent,
  NzTabSetComponent,
} from 'ng-zorro-antd/tabs';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { DatePipe, DecimalPipe, NgIf, SlicePipe } from '@angular/common';
import { NzButtonComponent, NzButtonModule } from 'ng-zorro-antd/button';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTagComponent } from 'ng-zorro-antd/tag';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { InputModule } from '@beautyapp/form';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import {
  NZ_DRAWER_DATA,
  NzDrawerModule,
  NzDrawerRef,
  NzDrawerService,
} from 'ng-zorro-antd/drawer';
import { addMinutes, format } from 'date-fns';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { catchError, Observable, of, throwError } from 'rxjs';
import { NzBadgeComponent } from 'ng-zorro-antd/badge';
import { NzCardComponent } from 'ng-zorro-antd/card';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { UserCardComponent } from './shared/user-card/user-card.component';
import { NzDividerComponent } from 'ng-zorro-antd/divider';
import { NzAlertComponent } from 'ng-zorro-antd/alert';
import {
  BookingService,
  BookingSourceService,
  EmployeeService,
  ServiceService,
  timeStrToSeconds,
  VisitService,
  VisitStatusPipe,
} from '@beautyapp/core';
import { defaultIfEmpty, map, switchMap } from 'rxjs/operators';
import { EhrListComponent } from './shared/ehr-list/ehr-list.component';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { VisitDisableReasonComponent } from '../components/visit-disable-reason/visit-disable-reason.component';
import { BookingListComponent } from './shared/booking-list/booking-list.component';
import { RealizationListComponent } from './shared/realization-list/realization-list.component';
import { Store } from '@ngxs/store';
import { VisitAddFacade } from '@beautyapp/shared';
import { VisitAddBookingComponent } from './shared/visit-add-booking/visit-add-booking.component';
import { RecipeSendMailComponent } from './shared/recipe-send-mail/recipe-send-mail.component';
import { SelectComponent } from '../../../../form/src/lib/select/select.component';
import { PaymentTypePipe } from '../../../../core/src/lib/pipes/payment-type/payment-type.pipe';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzOptionComponent } from 'ng-zorro-antd/select';
import { VisitAuditHistoryComponent } from './shared/visit-audit-history/visit-audit-history.component';
import { VisitCommentsComponent } from './shared/visit-comments/visit-comments.component';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NgFor } from '@angular/common';
import { Router } from '@angular/router';
import { OperationService, VisitOperationRow } from '@beautyapp/core';

import { canCompleteVisit } from '../utils/visit-completion';
import { deletedServiceNames } from './deleted-service-error';

@UntilDestroy()
@Component({
  selector: 'beautyapp-visit-add',
  templateUrl: './visit-manage.component.html',
  styleUrls: ['./visit-manage.component.scss'],
  imports: [
    NzTabSetComponent,
    NzTabComponent,
    NzIconDirective,
    NzButtonComponent,
    NzInputModule,
    NzGridModule,
    NzSpaceModule,
    NzTagComponent,
    ReactiveFormsModule,
    TranslatePipe,
    FormsModule,
    NgxPermissionsModule,
    SelectComponent,
    InputModule,
    NzButtonModule,
    NzBadgeComponent,
    NzCardComponent,
    NzModalModule,
    NzDrawerModule,
    UserCardComponent,
    NzDividerComponent,
    NzAlertComponent,
    VisitStatusPipe,
    EhrListComponent,
    BookingListComponent,
    RealizationListComponent,
    PaymentTypePipe,
    DatePipe,
    DecimalPipe,
    VisitAddBookingComponent,
    NzTabsComponent,
    NzSelectModule,
    NzOptionComponent,
    FormsModule,
    VisitAuditHistoryComponent,
    VisitCommentsComponent,
    SlicePipe,
    NzPopconfirmModule,
    NzTableModule,
    NgFor,
  ],
})
export class VisitManageComponent implements OnInit, OnDestroy {
  data = inject(NZ_DRAWER_DATA);
  store = inject(Store);
  drawerRef = inject(NzDrawerRef);
  visitAddFacade = inject(VisitAddFacade);
  modalService = inject(NzModalService);
  drawerService = inject(NzDrawerService);
  translateService = inject(TranslateService);
  serviceService = inject(ServiceService);
  bookingService = inject(BookingService);
  bookingSourceService = inject(BookingSourceService);
  visitService = inject(VisitService);
  notificationService = inject(NzNotificationService);
  permissionsService = inject(NgxPermissionsService);
  operationService = inject(OperationService);
  router = inject(Router);
  activeBranch = this.store.selectSignal((state) => state.auth.activeBranch);
  ehrActive = computed(() => this.activeBranch().ehrEnabled);
  showPayment = computed(() => {
    const isEmployee =
      !this.permissionsService.getPermission('calendar.view_all');
    return !isEmployee || this.activeBranch()?.employeePaymentEnabled;
  });

  services$: Observable<any> = this.serviceService.dropdown();

  form: UntypedFormGroup = this.visitAddFacade.form;

  paymentForm = this.visitAddFacade.paymentForm;

  nzTabPosition: NzTabPosition = 'left';
  selectedIndex = 0;

  // Component property
  isEditingDate = false;
  historyReloadCount = signal(0);
  commentsReloadCount = signal(0);
  bookingListReloadCount = signal(0);
  operations = signal<VisitOperationRow[]>([]);
  operationsLoading = signal(false);
  operationsLoaded = signal(false);
  ehrReload = signal<number | null>(null);
  visitDate = signal<Date | null>(null);

  employeeName = signal<string | null>(null);
  editingSource = signal(false);
  updatingSource = signal(false);
  selectedSourceId = signal<string | null>(null);

  updatingStatus = signal(false);
  statusOptions = [
    { value: 'PENDING', label: 'მოლოდინში' },
    { value: 'CONFIRMED', label: 'დადასტურებული' },
    { value: 'IN_PROGRESS', label: 'პროცესში' },
    { value: 'NO_SHOW', label: 'არ გამოცხადდა' },
    { value: 'NEED_CALL', label: 'დასარეკია' },
  ];

  bookingSourcesResource = rxResource({
    params: signal({}),
    stream: () =>
      this.bookingSourceService.getAll().pipe(
        defaultIfEmpty([]),
        catchError(() => of([]))
      ),
  });

  bookingSources = computed(() => {
    const sources = this.bookingSourcesResource.value() || [];
    // Include the current visit's source even if it's inactive
    const currentSource = this.visitAddFacade.visit()?.source;
    if (currentSource && !sources.find((s: any) => s.id === currentSource.id)) {
      return [currentSource, ...sources];
    }
    return sources;
  });

  isEmoji(icon: string): boolean {
    if (!icon) return false;
    // Check if the icon contains emoji characters or is a single character (emoji)
    // NZ-ZORRO icons are multi-character strings like 'phone', 'mobile', etc.
    const emojiRegex =
      /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]/u;
    return (
      emojiRegex.test(icon) || (icon.length <= 2 && !/^[a-z]+$/.test(icon))
    );
  }

  updateVisitSource(): void {
    const visitId = this.visitAddFacade.visitId;
    if (!visitId) return;

    const sourceId = this.selectedSourceId();

    this.updatingSource.set(true);

    this.visitService
      .updateSource(+visitId, sourceId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.updatingSource.set(false);
          this.editingSource.set(false);
          this.getVisit(+visitId);
          this.notificationService.success(
            this.translateService.instant('calendar.form.source'),
            this.translateService.instant(
              'bookingSources.messages.updateSuccess'
            )
          );
        },
        error: () => {
          this.updatingSource.set(false);
          this.notificationService.error(
            this.translateService.instant('calendar.form.source'),
            this.translateService.instant('bookingSources.messages.editError')
          );
        },
      });
  }

  startEditingSource(): void {
    this.selectedSourceId.set(this.visitAddFacade.visit()?.source?.id || null);
    this.editingSource.set(true);
  }

  cancelEditingSource(): void {
    this.selectedSourceId.set(null);
    this.editingSource.set(false);
  }

  onStatusChange(newStatus: string): void {
    const visitId = this.visitAddFacade.visitId;
    if (!visitId || this.updatingStatus()) return;

    this.updatingStatus.set(true);
    this.visitService
      .updateStatus(+visitId, newStatus)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.updatingStatus.set(false);
          this.getVisit(+visitId);
          this.historyReloadCount.update((c) => c + 1);
          this.notificationService.success(
            'სტატუსი',
            'სტატუსი წარმატებით შეიცვალა'
          );
        },
        error: (err: any) => {
          this.updatingStatus.set(false);
          const msg = err?.error?.message;
          const translated = msg
            ? this.translateService.instant('serverErrors.' + msg)
            : '';
          this.notificationService.error(
            'შეცდომა',
            translated && translated !== 'serverErrors.' + msg
              ? translated
              : msg || 'სტატუსის შეცვლა ვერ მოხერხდა'
          );
        },
      });
  }

  onOperationsTabSelected(): void {
    this.preloadOperations();
  }

  /**
   * Idempotent operations fetch. Called both eagerly on visit load (so the
   * Revert/Edit-completed buttons resolve correctly on first render) and on
   * Operations-tab click (legacy path). Returns early if already loaded or
   * if currently loading.
   */
  private preloadOperations(): void {
    if (this.operationsLoaded() || this.operationsLoading()) return;
    const visitId = +this.visitAddFacade.visitId;
    if (!Number.isFinite(visitId)) return;

    this.operationsLoading.set(true);
    this.operationService
      .getByVisit(visitId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (ops) => {
          this.operations.set(ops);
          this.operationsLoaded.set(true);
          this.operationsLoading.set(false);
        },
        error: () => {
          this.operations.set([]);
          this.operationsLoaded.set(true);
          this.operationsLoading.set(false);
        },
      });
  }

  onOperationClick(op: VisitOperationRow): void {
    this.router.navigate(['/operations', op.id]);
  }

  // ─────────────────────────────────────────────────────────────────
  // Payment type / operation status label maps for the Operations tab.
  // Source: paymentTypes in add-employee-appointment.component.ts.
  // ─────────────────────────────────────────────────────────────────
  private readonly PAYMENT_TYPE_LABELS: Record<number, string> = {
    1: 'ნაღდი ანგარიშსწორება',
    2: 'საბანკო ბარათი',
    3: 'ნაღდი + საბანკო',
    5: 'ბალანსი',
    6: 'ბარტერი',
    7: 'კონსიგნაცია',
  };

  paymentTypeLabel(value: any): string {
    if (value === null || value === undefined || value === '') return '—';
    return this.PAYMENT_TYPE_LABELS[+value] ?? String(value);
  }

  private readonly OPERATION_STATUS_LABELS: Record<number, string> = {
    0: 'მოლოდინში',
    1: 'დასრულებული',
    2: 'დასრულებული',
  };

  operationStatusLabel(value: any): string {
    if (value === null || value === undefined || value === '') return '—';
    return this.OPERATION_STATUS_LABELS[+value] ?? String(value);
  }

  /**
   * Pattern-translates a backend error message to a Georgian user-friendly
   * sentence. Falls back to the existing serverErrors.* i18n key lookup,
   * then to the raw message, then to a generic fallback.
   */
  private translateRevertError(
    rawMsg: string | undefined,
    fallback: string
  ): string {
    if (!rawMsg) return fallback;
    const opsLinked = rawMsg.match(
      /Cannot revert visit (\d+): (\d+) operation/i
    );
    if (opsLinked) {
      return `ვიზიტს აქვს ${opsLinked[2]} ოპერაცია — ვერ მოხერხდება მარტივი დაბრუნება. გამოიყენე "დასრულებული ვიზიტის რედაქტირება" ფუნქცია.`;
    }
    if (/Visit is not in COMPLETED state/i.test(rawMsg)) {
      return 'ვიზიტი არ არის დასრულებული — დაბრუნება შეუძლებელია.';
    }
    if (/Visit belongs to another branch/i.test(rawMsg)) {
      return 'ვიზიტი სხვა ფილიალს ეკუთვნის.';
    }
    if (/Visit \d+ not found|Visit not found/i.test(rawMsg)) {
      return 'ვიზიტი ვერ მოიძებნა.';
    }
    const i18nKey = 'serverErrors.' + rawMsg;
    const translated = this.translateService.instant(i18nKey);
    if (translated && translated !== i18nKey) return translated;
    return rawMsg || fallback;
  }

  revertingCompletion = signal(false);

  /**
   * Single Revert button (Option B): visible whenever the visit is COMPLETED
   * AND the user has authority to revert. Backward-compatible:
   *   - any user with the Admin role passes (legacy behavior), OR
   *   - a user granted the `visit_revert` permission via a custom role.
   * Mirrors the backend gate in webapp-ge visit.controller.ts so the button
   * isn't shown only to 403 on click.
   */
  canRevert(): boolean {
    if (this.visitAddFacade.visit()?.status !== 'COMPLETED') return false;
    return (
      !!this.permissionsService.getPermission('Admin') ||
      !!this.permissionsService.getPermission('visit_revert')
    );
  }

  onRevertClick(): void {
    if (this.revertingCompletion()) return;

    if (!this.operationsLoaded()) {
      const visitId = +this.visitAddFacade.visitId;
      if (!Number.isFinite(visitId)) return;
      this.operationsLoading.set(true);
      this.operationService
        .getByVisit(visitId)
        .pipe(untilDestroyed(this))
        .subscribe({
          next: (ops) => {
            this.operations.set(ops);
            this.operationsLoaded.set(true);
            this.operationsLoading.set(false);
            this.openRevertConfirm();
          },
          error: () => {
            this.operations.set([]);
            this.operationsLoaded.set(true);
            this.operationsLoading.set(false);
            this.openRevertConfirm();
          },
        });
      return;
    }

    this.openRevertConfirm();
  }

  private openRevertConfirm(): void {
    const opCount = this.operations().length;
    if (opCount === 0) {
      this.modalService.confirm({
        nzTitle: 'დასრულებული ვიზიტის დაბრუნება?',
        nzContent:
          'ვიზიტი დაბრუნდება "მიმდინარე" სტატუსზე და ჩაწერები გადავა "ჩაწერილი" სტატუსზე. გაგრძელება?',
        nzOkText: 'გაგრძელება',
        nzOkDanger: true,
        nzCancelText: 'გაუქმება',
        nzOnOk: () => this.doRevertEmpty(),
      });
    } else {
      this.modalService.confirm({
        nzTitle: 'დასრულებული ვიზიტის რედაქტირება?',
        nzContent: `ვიზიტს აქვს ${opCount} ოპერაცია. რევერტი წაშლის ოპერაციებს, ხელფასებს და კონსიგნაციებს. გადახდები შენარჩუნდება Draft-ის სახით. გაგრძელება?`,
        nzOkText: 'გაგრძელება',
        nzOkDanger: true,
        nzCancelText: 'გაუქმება',
        nzOnOk: () => this.doRevertWithOps(),
      });
    }
  }

  private doRevertEmpty(): void {
    const visitId = +this.visitAddFacade.visitId;
    if (!visitId) return;

    this.revertingCompletion.set(true);
    this.visitService
      .revertEmptyCompleted(visitId, { reason: '' })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.revertingCompletion.set(false);
          this.notificationService.success(
            'ვიზიტი',
            'ვიზიტის დასრულება გაუქმდა'
          );
          this.getVisit(visitId);
          this.historyReloadCount.update((c) => c + 1);
        },
        error: (err: any) => {
          this.revertingCompletion.set(false);
          this.notificationService.error(
            'შეცდომა',
            this.translateRevertError(
              err?.error?.message,
              'ვიზიტის დაბრუნება ვერ მოხერხდა'
            )
          );
        },
      });
  }

  private doRevertWithOps(): void {
    const visitId = +this.visitAddFacade.visitId;
    if (!visitId) return;

    this.revertingCompletion.set(true);
    this.visitService
      .revertCompletedWithOperations(visitId, { reason: '' })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.revertingCompletion.set(false);
          this.notificationService.success(
            'ვიზიტი',
            'დასრულებული ვიზიტი დაბრუნდა რედაქტირებისთვის'
          );
          this.getVisit(visitId);
          this.operationsLoaded.set(false);
          this.operations.set([]);
          this.historyReloadCount.update((c) => c + 1);
        },
        error: (err: any) => {
          this.revertingCompletion.set(false);
          this.notificationService.error(
            'შეცდომა',
            this.translateRevertError(
              err?.error?.message,
              'ვიზიტის რედაქტირება ვერ მოხერხდა'
            )
          );
        },
      });
  }

  onSourceChange(sourceId: string | null): void {
    console.log('==== SOURCE CHANGE EVENT ====');
    console.log('Previous value:', this.selectedSourceId());
    console.log('New value:', sourceId);
    this.selectedSourceId.set(sourceId);
    console.log('After set:', this.selectedSourceId());
    console.log('===========================');
  }

  roomId = signal<string | null>(null);
  employeeId = signal<string | null>(null);

  // True when current form amount + saved total >= total owed
  // True when what is already settled, plus what is on the form, covers what
  // the visit owes — in both currencies. The rule itself lives in
  // visit-completion.ts, where it can be tested: this component needs the
  // store, the router and a modal service before it will even build.
  get canCompleteWithCurrentPayment(): boolean {
    return canCompleteVisit(this.paymentForm.value, {
      totalPayPrice: this.visitAddFacade.totalPayPrice(),
      totalPaid: this.visitAddFacade.totalPaid(),
      totalConsignation: this.visitAddFacade.totalConsignation(),
      totalMinutes: this.visitAddFacade.totalMinutes(),
      totalMinutesPaid: this.visitAddFacade.totalMinutesPaid(),
    });
  }

  // Computed property to check if booking date is in the future
  isBookingDateFuture = computed(() => {
    const bookingDate = this.visitDate();
    if (!bookingDate) return false;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const booking = new Date(bookingDate);
    booking.setHours(0, 0, 0, 0);

    return booking > today;
  });

  private updateTabPositionBasedOnScreenSize(): void {
    if (window.innerWidth <= 768) {
      // Mobile screen size
      this.nzTabPosition = 'top';
    } else {
      this.nzTabPosition = 'left';
    }
  }

  get isAnonymous() {
    return this.form.get('isAnonymous')?.value;
  }

  constructor() {
    effect(() => {
      if (this.visitAddFacade.totalMinutes() > 0) {
        this.setMinutes(this.visitAddFacade.totalMinutes());
        if (this.visitAddFacade.totalPrice() === 0) {
          this.paymentForm.get('paymentType')?.setValue(4);
        } else {
          this.paymentForm.get('paymentType')?.setValue(null);
        }
      }
    });

    // Initial check for screen size
    this.updateTabPositionBasedOnScreenSize();

    // Add resize event listener
    window.addEventListener('resize', () => {
      this.updateTabPositionBasedOnScreenSize();
    });
  }

  ngOnInit() {
    // beaut-366: preload a first page of customers so the customer select shows
    // records as soon as it is focused (empty search → BE returns the first 50),
    // instead of the empty "no data" state. reset() (ngOnDestroy) clears it again.
    this.visitAddFacade.searchCustomer('');
    if (this.data) {
      if (this.data.date) {
        this.form
          .get('bookingDate')
          ?.setValue(format(new Date(this.data.date), 'yyyy-MM-dd'));
        this.form
          .get('startTime')
          ?.setValue(format(new Date(this.data.date), 'yyyy-MM-dd HH:mm'));
      }
    }

    if (this.data && this.data.employeeId) {
      console.log(this.data.employeeId);
    }

    this.form
      .get('isAnonymous')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        if (value) {
          this.form.get('userId')?.clearValidators();
          this.form.get('userId')?.updateValueAndValidity();
          this.form.get('sendNotification')?.setValue(true);
          this.form.get('anonymousName')?.setValidators(Validators.required);
          this.form.get('anonymousName')?.updateValueAndValidity();
        } else {
          this.form.get('userId')?.setValue(null);
          this.form.get('sendNotification')?.setValue(false);
          this.form.get('userId')?.setValidators(Validators.required);
          this.form.get('userId')?.updateValueAndValidity();
          this.form.get('anonymousName')?.clearValidators();
          this.form.get('anonymousName')?.updateValueAndValidity();
        }
      });

    if (this.data && this.data.visitId) {
      this.getVisit(this.data.visitId);
    }

    this.paymentForm
      .get('paymentType')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((value) => {
        // Use remainingAmount for partial payments; falls back to totalPayPrice when no saved payments exist
        const amount = this.visitAddFacade.remainingAmount();
        if (value) {
          this.paymentForm.get('price')?.setValue(amount);

          switch (value) {
            case 1:
              this.setCash(amount);
              this.setCard(0);
              this.setBalance(0);
              this.setBankTransfer(0);
              this.paymentForm.get('barter')?.setValue(0);
              this.paymentForm.get('consignation')?.setValue(0);
              break;
            case 2:
              this.setCash(0);
              this.setBalance(0);
              this.setBankTransfer(0);
              this.setCard(amount);
              this.paymentForm.get('barter')?.setValue(0);
              this.paymentForm.get('consignation')?.setValue(0);
              break;
            case 5:
              this.setCash(0);
              this.setCard(0);
              this.setBankTransfer(0);
              this.setBalance(amount);
              this.paymentForm.get('barter')?.setValue(0);
              this.paymentForm.get('consignation')?.setValue(0);
              break;
            case 8:
              this.setCash(0);
              this.setCard(0);
              this.setBalance(0);
              this.setBankTransfer(amount);
              this.paymentForm.get('barter')?.setValue(0);
              this.paymentForm.get('consignation')?.setValue(0);
              break;
            case 9:
              this.setCash(0);
              this.setCard(0);
              this.setBalance(0);
              this.setBankTransfer(0);
              this.paymentForm.get('barter')?.setValue(0);
              this.paymentForm.get('consignation')?.setValue(0);
              break;
            case 6:
              this.setCash(0);
              this.setCard(0);
              this.setBalance(0);
              this.setBankTransfer(0);
              this.paymentForm.get('barter')?.setValue(0);
              this.paymentForm.get('consignation')?.setValue(0);
              break;
            case 7:
              this.setCash(0);
              this.setCard(0);
              this.setBalance(0);
              this.setBankTransfer(0);
              this.paymentForm.get('barter')?.setValue(0);
              this.paymentForm.get('consignation')?.setValue(amount);
              break;
            case 4:
              this.setCash(0);
              this.setCard(0);
              this.setBalance(0);
              this.setBankTransfer(0);
              this.paymentForm.get('barter')?.setValue(0);
              this.paymentForm.get('consignation')?.setValue(0);
              this.setMinutes(this.visitAddFacade.totalMinutes());
              break;
            default:
              this.setCash(0);
              this.setCard(0);
              this.setBalance(0);
              this.setBankTransfer(0);
              this.paymentForm.get('barter')?.setValue(0);
              this.paymentForm.get('consignation')?.setValue(0);
              break;
          }
        }
      });
  }

  setCash(totalPrice: number) {
    if (totalPrice > 0) {
      this.paymentForm.get('cash')?.setValidators(Validators.required);
      this.paymentForm.get('cash')?.updateValueAndValidity();
      this.paymentForm.get('cash')?.setValue(totalPrice);
    } else {
      this.paymentForm.get('cash')?.clearValidators();
      this.paymentForm.get('cash')?.updateValueAndValidity();
      this.paymentForm.get('cash')?.setValue(0);
    }
  }

  setCard(totalPrice: number) {
    if (totalPrice > 0) {
      this.paymentForm.get('card')?.setValidators(Validators.required);
      this.paymentForm.get('card')?.updateValueAndValidity();
      this.paymentForm.get('card')?.setValue(totalPrice);
    } else {
      this.paymentForm.get('card')?.clearValidators();
      this.paymentForm.get('card')?.updateValueAndValidity();
      this.paymentForm.get('card')?.setValue(0);
    }
  }

  setBalance(totalPrice: number) {
    if (totalPrice > 0) {
      this.paymentForm.get('balance')?.setValidators(Validators.required);
      this.paymentForm.get('balance')?.updateValueAndValidity();
      this.paymentForm.get('balance')?.setValue(totalPrice);
    } else {
      this.paymentForm.get('balance')?.clearValidators();
      this.paymentForm.get('balance')?.updateValueAndValidity();
      this.paymentForm.get('balance')?.setValue(0);
    }
  }

  setBankTransfer(totalPrice: number) {
    if (totalPrice > 0) {
      this.paymentForm.get('bankTransfer')?.setValidators(Validators.required);
      this.paymentForm.get('bankTransfer')?.updateValueAndValidity();
      this.paymentForm.get('bankTransfer')?.setValue(totalPrice);
    } else {
      this.paymentForm.get('bankTransfer')?.clearValidators();
      this.paymentForm.get('bankTransfer')?.updateValueAndValidity();
      this.paymentForm.get('bankTransfer')?.setValue(0);
    }
  }

  setMinutes(totalPrice: number) {
    if (totalPrice > 0) {
      this.paymentForm.get('minutes')?.setValue(totalPrice);
      this.paymentForm
        .get('minutes')
        ?.setValidators([
          Validators.required,
          Validators.min(totalPrice),
          Validators.max(totalPrice),
        ]);
      this.paymentForm.get('minutes')?.updateValueAndValidity();

      console.log(this.paymentForm.get('minutes'));
    } else {
      this.paymentForm.get('minutes')?.clearValidators();
      this.paymentForm.get('minutes')?.updateValueAndValidity();
      this.paymentForm.get('minutes')?.setValue(0);
    }
  }

  getVisit(visitId?: number) {
    if (!visitId) {
      this.visitAddFacade.loading.set(false);
      return;
    }

    this.visitAddFacade
      .getVisitById(visitId)
      .pipe(untilDestroyed(this))
      .subscribe((visit) => {
        this.visitAddFacade.visit.set(visit);
        this.visitDate.set(visit.bookingDate);
        this.form.patchValue({
          ...visit,
          bookingDate: format(new Date(visit.bookingDate), 'yyyy-MM-dd'),
          startTime: format(
            new Date(visit.bookingDate + ' ' + visit.startTime),
            'yyyy-MM-dd HH:mm'
          ),
          endTime: format(
            new Date(visit.bookingDate + ' ' + visit.endTime),
            'yyyy-MM-dd HH:mm'
          ),
          onUserRequest: visit.onUserRequest,
        });

        this.visitAddFacade.paymentForm.patchValue({
          visitId: visit.id,
        });

        this.form.get('sendNotification')?.setValue(visit.sendNotification);
        this.visitAddFacade.user.set(visit.user);
        this.visitAddFacade.userId = visit.userId;

        this.paymentForm.patchValue({
          visitId: visit.id,
          cash: visit.cash,
          card: visit.card,
          balance: visit.balance,
          bankTransfer: visit.bankTransfer,
          bankTransferBank: visit.bankTransferBank,
          minutes: visit.minutes,
          bank: visit.bank,
        });

        if (visit.paymentType) {
          this.paymentForm.get('paymentType')?.setValue(visit.paymentType);
        } else {
          if (
            this.visitAddFacade.totalMinutes() > 0 &&
            this.visitAddFacade.totalPayPrice() === 0
          ) {
            this.paymentForm.get('paymentType')?.setValue(4);
          }
        }

        // Load saved payments for the visit
        this.visitAddFacade
          .loadPayments(visit.id)
          .pipe(untilDestroyed(this))
          .subscribe();

        // Eagerly preload operations so canRevert() / canEditCompleted()
        // resolve deterministically on first render. Without this, the Revert
        // button would show on completed visits with operations until the
        // user opened the Operations tab — then disappear, which is confusing.
        if (visit.status === 'COMPLETED') {
          this.preloadOperations();
        }

        if (this.visitAddFacade.isCompleted) {
          this.selectedIndex = 3;
        }
        this.visitAddFacade.loading.set(false);
        this.ehrReload.update((res) => (res ? res + 1 : 1));
        this.historyReloadCount.update((res) => res + 1);
        // beaut-377: keep the booking list in sync with the backend after a
        // reload (e.g. a customer change that removes the old customer's
        // package-covered booking services).
        this.bookingListReloadCount.update((res) => res + 1);
      });
  }

  cancel() {
    this.modalService.create({
      nzTitle: 'ვიზიტის გაუქმება',
      nzContent: VisitDisableReasonComponent,
      nzData: {
        visitId: this.visitAddFacade.visitId,
        paymentsCount: this.visitAddFacade.visitPayments().length,
        totalPaid: this.visitAddFacade.totalPaid(),
      },
      nzOnOk: () => {
        this.drawerRef.close();
      },
    });
  }

  ngOnDestroy(): void {
    this.visitAddFacade.reset();

    // Remove the resize event listener on component destroy
    window.removeEventListener('resize', () => {
      this.updateTabPositionBasedOnScreenSize();
    });
  }

  addPayment() {
    this.paymentForm.markAllAsTouched();

    if (this.paymentForm.invalid) {
      if (this.paymentForm.hasError('totalPrice')) {
        this.notificationService.error(
          this.translateService.instant('addVisit.errors.paymentTitle'),
          this.translateService.instant('addVisit.errors.totalPriceMismatch')
        );
      }
      return;
    }
    this.visitAddFacade.loading.set(true);
    this.visitAddFacade
      .addPayment()
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          this.visitAddFacade.loading.set(false);
          return throwError(() => error);
        })
      )
      .subscribe((res) => {
        this.getVisit(res.id);
      });
  }

  printReceipt() {
    this.visitAddFacade.printReceipt();
  }

  sendRecipe() {
    const modal = this.modalService.create({
      nzTitle: this.translateService.instant('addVisit.sendRecipe.title'),
      nzContent: RecipeSendMailComponent,
    });
    modal.componentInstance?.email?.set(
      this.visitAddFacade.user()?.email || ''
    );
  }

  /**
   * Recompute the price field from actual entered payment method values.
   * For partial payments, price = sum of entered methods (not the full remaining).
   */
  private recomputePaymentPrice() {
    const cash = +(this.paymentForm.get('cash')?.value || 0);
    const card = +(this.paymentForm.get('card')?.value || 0);
    const balance = +(this.paymentForm.get('balance')?.value || 0);
    const bankTransfer = +(this.paymentForm.get('bankTransfer')?.value || 0);
    const barter = +(this.paymentForm.get('barter')?.value || 0);
    const consignation = +(this.paymentForm.get('consignation')?.value || 0);
    const computed =
      cash + card + balance + bankTransfer + barter + consignation;
    this.paymentForm.get('price')?.setValue(computed, { emitEvent: false });
  }

  savePayment() {
    this.recomputePaymentPrice();
    this.paymentForm.markAllAsTouched();

    if (this.paymentForm.invalid) {
      if (this.paymentForm.hasError('totalPrice')) {
        this.notificationService.error(
          this.translateService.instant('addVisit.errors.paymentTitle'),
          this.translateService.instant('addVisit.errors.totalPriceMismatch')
        );
      }
      return;
    }

    this.visitAddFacade.loading.set(true);
    this.visitAddFacade
      .saveVisitPayment()
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          this.visitAddFacade.loading.set(false);
          const msg = error?.error?.message;
          const translated = msg
            ? this.translateService.instant('serverErrors.' + msg)
            : '';
          this.notificationService.error(
            'შეცდომა',
            translated && translated !== 'serverErrors.' + msg
              ? translated
              : msg || 'გადახდის შენახვა ვერ მოხერხდა'
          );
          return throwError(() => error);
        })
      )
      .subscribe(() => {
        this.visitAddFacade.loading.set(false);
        this.notificationService.success(
          'გადახდა',
          'გადახდა წარმატებით შეინახა'
        );
      });
  }

  /**
   * #2908 §10: if completion was rejected because a used package is expired for
   * its booking, show a confirm; on OK re-run the same completion with the
   * acknowledge flag; on cancel, leave the visit open. Returns true if it
   * handled the error (caller should stop the generic error path).
   */
  private handleExpiredPackage(error: any, retry: () => void): boolean {
    if (error?.error?.code !== 'EXPIRED_PACKAGE_USAGE') return false;
    this.modalService.confirm({
      nzTitle: this.translateService.instant(
        'servicePackages.editModal.expiredPackageTitle'
      ),
      nzContent:
        error.error.message ||
        this.translateService.instant(
          'servicePackages.editModal.expiredPackageContent'
        ),
      nzOkText: this.translateService.instant(
        'servicePackages.editModal.expiredPackageConfirm'
      ),
      nzCancelText: this.translateService.instant('buttons.cancel'),
      nzOnOk: () => retry(),
      nzOnCancel: () => this.visitAddFacade.loading.set(false),
    });
    return true;
  }

  /**
   * beaut-635: completion was refused because a line is on a deleted service.
   * No retry -- staff must replace or remove the line first. Returns true if it
   * handled the error (caller should stop the generic error path).
   */
  private handleDeletedService(error: any): boolean {
    const names = deletedServiceNames(error);
    if (names === null) return false;
    this.visitAddFacade.loading.set(false);
    const title = this.translateService.instant(
      'addVisit.errors.deletedServiceTitle'
    );
    // No names in the refusal: show the server's text, not an empty list.
    const content = names.length
      ? this.translateService.instant('addVisit.errors.deletedServiceContent', {
          names: names.join(', '),
        })
      : error?.error?.message || title;
    this.modalService.error({ nzTitle: title, nzContent: content });
    return true;
  }

  saveAndComplete(confirmExpiredPackage = false) {
    this.recomputePaymentPrice();
    this.paymentForm.markAllAsTouched();

    if (this.paymentForm.invalid) {
      if (this.paymentForm.hasError('totalPrice')) {
        this.notificationService.error(
          this.translateService.instant('addVisit.errors.paymentTitle'),
          this.translateService.instant('addVisit.errors.totalPriceMismatch')
        );
      }
      return;
    }

    this.visitAddFacade.loading.set(true);
    this.visitAddFacade
      .saveAndCompleteVisit(confirmExpiredPackage)
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          if (
            this.handleExpiredPackage(error, () => this.saveAndComplete(true))
          ) {
            return throwError(() => error);
          }
          if (this.handleDeletedService(error)) {
            return throwError(() => error);
          }
          this.visitAddFacade.loading.set(false);
          const msg = error?.error?.message;
          const translated = msg
            ? this.translateService.instant('serverErrors.' + msg)
            : '';
          this.notificationService.error(
            'შეცდომა',
            translated && translated !== 'serverErrors.' + msg
              ? translated
              : msg || 'ვიზიტის დასრულება ვერ მოხერხდა'
          );
          return throwError(() => error);
        })
      )
      .subscribe((res) => {
        this.getVisit(res.id || +this.visitAddFacade.visitId);
        this.notificationService.success(
          'ვიზიტი',
          'ვიზიტი წარმატებით დასრულდა'
        );
      });
  }

  completeVisit(confirmExpiredPackage = false) {
    this.visitAddFacade.loading.set(true);
    this.visitAddFacade
      .completeVisit(confirmExpiredPackage)
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          if (
            this.handleExpiredPackage(error, () => this.completeVisit(true))
          ) {
            return throwError(() => error);
          }
          if (this.handleDeletedService(error)) {
            return throwError(() => error);
          }
          this.visitAddFacade.loading.set(false);
          const msg = error?.error?.message;
          const translated = msg
            ? this.translateService.instant('serverErrors.' + msg)
            : '';
          this.notificationService.error(
            'შეცდომა',
            translated && translated !== 'serverErrors.' + msg
              ? translated
              : msg || 'ვიზიტის დასრულება ვერ მოხერხდა'
          );
          return throwError(() => error);
        })
      )
      .subscribe((res) => {
        this.getVisit(res.id || +this.visitAddFacade.visitId);
        this.notificationService.success(
          'ვიზიტი',
          'ვიზიტი წარმატებით დასრულდა'
        );
      });
  }

  deletePayment(paymentId: number) {
    const visitId = +this.visitAddFacade.visitId;
    this.visitAddFacade.loading.set(true);
    this.visitAddFacade
      .deleteVisitPayment(visitId, paymentId)
      .pipe(
        untilDestroyed(this),
        catchError((error) => {
          this.visitAddFacade.loading.set(false);
          const msg = error?.error?.message;
          const translated = msg
            ? this.translateService.instant('serverErrors.' + msg)
            : '';
          this.notificationService.error(
            'შეცდომა',
            translated && translated !== 'serverErrors.' + msg
              ? translated
              : msg || 'გადახდის წაშლა ვერ მოხერხდა'
          );
          return throwError(() => error);
        })
      )
      .subscribe(() => {
        this.visitAddFacade.loading.set(false);
        this.notificationService.success(
          'გადახდა',
          'გადახდა წარმატებით წაიშალა'
        );
      });
  }
}
